/**
 * Person (aka Member) — the core user/person entity.
 *
 * In the backend the DynamoDB table stores records as `DynamoPerson`,
 * and the domain entity is called **Person**. They map 1:1.
 *
 * Almost every field is optional on the backend.
 * Dates arrive as ISO-8601 strings from the API and need parsing on the
 * frontend side.
 *
 * @todo Replace with source of truth from `@enrollment-platform-service` package.
 */

/* -------------------------------------------------------------------------- */
/*                                   Enums                                    */
/* -------------------------------------------------------------------------- */

import { PersonRelationshipEnum as PersonRelationship } from '@surecompanies/contracts'

export { PersonRelationship }

export enum PhoneType {
  Mobile = 'Mobile',
  Home = 'Home',
  Work = 'Work',
}

export enum PersonRole {
  PlatformEmployee = 'PlatformEmployee',
  PlatformClientAdmin = 'PlatformClientAdmin',
  PlatformHixmeAdmin = 'PlatformHixmeAdmin',
  PlatformBroker = 'PlatformBroker',
}

export enum PersonStatusEnum {
  FULL_TIME = 'Full-Time',
  PART_TIME = 'Part-Time',
  RETIRED = 'Retired',
  TERMINATED = 'Terminated',
  TERMINATED_NO_COBRA = 'Terminated No Cobra',
  LEAVE = 'Leave',
  NON_PAID_LEAVE_OF_ABSENCE = 'Non-paid LOA',
}

/* -------------------------------------------------------------------------- */
/*                              Person Interface                              */
/* -------------------------------------------------------------------------- */

export interface Person {
  /* -------------------------------- Identity -------------------------------- */
  id: string
  employeePublicKey?: string
  clientPublicKey?: string
  firstName?: string
  middleName?: string
  lastName?: string
  fullName?: string
  dateOfBirth?: Date
  gender?: 'Male' | 'Female' | ''
  relationship?: PersonRelationship | string
  marriageStatus?: string
  marriageDate?: Date
  smoker?: boolean
  ssn?: string
  ssnlastFour?: string
  citizenship?: string

  /* ----------------------------- Contact & Address --------------------------- */
  emailAddress?: string
  phoneNumber?: string
  phoneType?: string
  alternatePhoneNumber?: string
  alternatePhoneType?: string
  streetAddress?: string
  streetAddressExt?: string
  city?: string
  county?: string
  stateProvince?: string
  postalCode?: string
  mailingStreetAddress?: string
  mailingStreetAddressExt?: string
  mailingCity?: string
  mailingCounty?: string
  mailingStateProvince?: string
  mailingPostalCode?: string
  mailingAddressSameAsResidential?: boolean
  addressSameAsParent?: boolean

  /* ------------------------------- Employment -------------------------------- */
  companyName?: string
  companyID?: string
  companyCode?: string
  companyEmployeeId?: string
  companyEmployeeIdEncrypted?: string
  employmentStatus?: string
  employmentType?: string
  hireDate?: Date
  rawHireDate?: string
  originalHireDate?: Date
  terminationDate?: Date
  jobTitle?: string
  jobFunction?: string
  department?: string
  departmentCode?: string
  division?: string
  divisionCode?: string
  salary?: string
  annualIncome?: string
  hourlyWage?: string
  payrollCycle?: number
  payType?: string
  isExempt?: boolean
  userRole?: PersonRole | string
  professionalLevel?: string
  hrisEmployeeId?: string

  /* ----------------------------- Benefits & Enrollment ----------------------- */
  benefitsEligible?: boolean
  benefitsEffectiveDate?: Date
  benefitEffectiveDate?: Date
  benefitsEffectiveDateNote?: string
  benefitClass?: string
  contributionGroup?: string
  contributionGroupOld?: string
  enrollmentStartDate?: Date
  enrollmentEndDate?: Date
  enrollmentCreated?: boolean
  enrollmentNote?: string
  reimbursementEligible?: boolean
  includedInMedical?: boolean
  existingMedicalPlanName?: string
  existingMedicalPlanWaived?: boolean
  existingMedicalPlanEnrollmentTier?: string
  previousCoverageEndDate?: Date
  premiumTaxCredit?: boolean
  exchangePlan?: boolean
  enrollMeStatus?: string

  /* ---------------------------------- COBRA ---------------------------------- */
  cobraStatus?: string
  cobraBenefitsEffectiveDate?: Date
  cobraBenefitsEligibleStartDate?: Date
  cobraBenefitsEligibleEndDate?: Date
  cobraMonthsOnExisting?: number
  cobraLetterPostDate?: Date
  cobraContinueBenefits?: string

  /* ------------------------------ Status Flags ------------------------------- */
  isActive?: boolean
  isRegistered?: boolean
  isTestUser?: boolean
  isValid?: boolean
  isDuplicate?: boolean
  isDisabled?: boolean
  isMedicareEligible?: boolean
  isMedicareAlreadyCovered?: boolean
  identityVerified?: boolean
  isVerified?: boolean
  isEmployed?: string
  isEligibleForEmployeerPaidBenefits?: boolean

  /* ----------------------- Eligibility Custom Fields ------------------------- */
  eligibility1?: string
  eligibility2?: string
  eligibility3?: string
  eligibility4?: string
  eligibility5?: string
  eligibility6?: string

  /* --------------------------------- Other ---------------------------------- */
  hixmeEmailAlias?: string
  emailAddressType?: string
  ratingArea?: string
  socialSecurityPayments?: boolean
  socalSecurityPayments?: boolean
  accountCreated?: string
  ssnhmac?: string
  ssnencrypted?: string
  disabilities?: string
  disabilityDocumentations?: string
  identityCreated?: boolean
  verifiedPersonalInfo?: boolean
  invitedDate?: Date
  updatedDate?: Date
  importPublicKey?: string
  batchPublicKey?: string
  row?: number
  ichraEmailSendLambdaTrigger?: boolean
  customField1?: string
  customField3?: string
  customField4?: string
  administeredClients?: string[]
  action?: string
  oldPayrollCycle?: number
  altPhoneNumber?: string
  altPhoneType?: string
}

/* -------------------------------------------------------------------------- */
/*                       DynamoPerson (DynamoDB Schema)                       */
/* -------------------------------------------------------------------------- */

export interface DynamoPersonKey {
  Id: string
}

export interface DynamoPerson extends DynamoPersonKey {
  /* -------------------------------- Identity -------------------------------- */
  EmployeePublicKey?: string
  ClientPublicKey?: string
  FirstName?: string
  MiddleName?: string
  LastName?: string
  FullName?: string
  DateOfBirth?: string
  Gender?: 'Male' | 'Female' | ''
  Relationship?: PersonRelationship | string
  MarriageStatus?: string
  MarriageDate?: string
  Smoker?: boolean
  Ssn?: string
  SsnLastFour?: string
  Citizenship?: string

  /* ----------------------------- Contact & Address --------------------------- */
  EmailAddress?: string
  // Sign-in email from AWS Cognito. Not on the legacy person GET; Coverage
  // Summary loads it from EPS identity. Present if a later person payload
  // includes it.
  CognitoEmail?: string
  PhoneNumber?: string
  PhoneType?: string
  AlternatePhoneNumber?: string
  AlternatePhoneType?: string
  StreetAddress?: string
  StreetAddressExt?: string
  City?: string
  County?: string
  StateProvince?: string
  PostalCode?: string
  MailingStreetAddress?: string
  MailingStreetAddressExt?: string
  MailingCity?: string
  MailingCounty?: string
  MailingStateProvince?: string
  MailingPostalCode?: string
  MailingAddressSameAsResidential?: boolean
  AddressSameAsParent?: boolean

  /* ------------------------------- Employment -------------------------------- */
  CompanyName?: string
  CompanyID?: string
  CompanyCode?: string
  CompanyEmployeeId?: string
  CompanyEmployeeIdEncrypted?: string
  EmploymentStatus?: string
  EmploymentType?: string
  HireDate?: string
  RawHireDate?: string
  OriginalHireDate?: string
  RehireDate?: string | null
  TerminationDate?: string
  JobTitle?: string
  JobFunction?: string
  Department?: string
  DepartmentCode?: string
  Division?: string
  DivisionCode?: string
  Salary?: string
  AnnualIncome?: string
  HourlyWage?: string
  PayrollCycle?: number
  PayType?: string
  IsExempt?: boolean
  UserRole?: PersonRole | string
  ProfessionalLevel?: string
  HrisEmployeeId?: string

  /* ----------------------------- Benefits & Enrollment ----------------------- */
  BenefitsEligible?: boolean
  BenefitsEffectiveDate?: string
  BenefitEffectiveDate?: string
  BenefitsEffectiveDateNote?: string
  BenefitClass?: string
  ContributionGroup?: string
  ContributionGroupOld?: string
  EnrollmentStartDate?: string
  EnrollmentEndDate?: string
  EnrollmentCreated?: boolean
  EnrollmentNote?: string
  ReimbursementEligible?: boolean
  IncludedInMedical?: boolean
  ExistingMedicalPlanName?: string
  ExistingMedicalPlanWaived?: boolean
  ExistingMedicalPlanEnrollmentTier?: string
  PreviousCoverageEndDate?: string
  PremiumTaxCredit?: boolean
  ExchangePlan?: boolean
  EnrollMeStatus?: string

  /* ---------------------------------- COBRA ---------------------------------- */
  CobraStatus?: string
  CobraBenefitsEffectiveDate?: string
  CobraBenefitsEligibleStartDate?: string
  CobraBenefitsEligibleEndDate?: string
  CobraMonthsOnExisting?: number
  CobraLetterPostDate?: string
  CobraContinueBenefits?: string

  /* ------------------------------ Status Flags ------------------------------- */
  IsActive?: boolean
  IsRegistered?: boolean
  IsTestUser?: boolean
  IsValid?: boolean
  IsDuplicate?: boolean
  IsDisabled?: boolean
  IsMedicareEligible?: boolean
  IsMedicareAlreadyCovered?: boolean
  IdentityVerified?: boolean
  IsVerified?: boolean
  IsEmployed?: string
  IsEligibleForEmployeerPaidBenefits?: boolean

  /* ----------------------- Eligibility Custom Fields ------------------------- */
  Eligibility1?: string
  Eligibility2?: string
  Eligibility3?: string
  Eligibility4?: string
  Eligibility5?: string
  Eligibility6?: string

  /* --------------------------------- Other ---------------------------------- */
  HixmeEmailAlias?: string
  EmailAddressType?: string
  RatingArea?: string
  SocialSecurityPayments?: boolean
  SocalSecurityPayments?: boolean
  AccountCreated?: string
  Ssnhmac?: string
  Ssnencrypted?: string
  Disabilities?: string
  DisabilityDocumentations?: string
  IdentityCreated?: boolean
  VerifiedPersonalInfo?: boolean
  InvitedDate?: string
  UpdatedDate?: string
  ImportPublicKey?: string
  BatchPublicKey?: string
  Row?: number
  IchraEmailSendLambdaTrigger?: boolean
  CustomField1?: string
  CustomField3?: string
  CustomField4?: string
  AdministeredClients?: string[]
  Action?: string
  OldPayrollCycle?: number
  AltPhoneNumber?: string
  AltPhoneType?: string
}
